import {
    AudioFile as AudioIcon,
    CheckCircle as CheckCircleIcon,
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Description as DocumentIcon,
    Error as ErrorIcon,
    FilePresent as FilePresentIcon,
    Image as ImageIcon,
    VideoFile as VideoIcon
} from "@mui/icons-material";
import Logger from "../../../services/Logger";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    LinearProgress,
    Paper,
    Snackbar,
    Typography
} from "@mui/material";
import React, { useState } from "react";
import S3Service from "../../../services/S3Service";
const FileUploadMain = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadResult, setUploadResult] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setUploadResult(null);
            setUploadProgress(0);
            
            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setFilePreview({
                url: previewUrl,
                type: file.type
            });
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            showSnackbar('Please select a file first', 'error');
            return;
        }

        try {
            setUploading(true);
            setUploadProgress(0);
            setUploadResult(null);

            // Simulate progress (since S3 upload doesn't provide progress callbacks)
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 200);

            // Generate unique filename
            const timestamp = Date.now();
            const fileExtension = selectedFile.name.split('.').pop();
            const fileName = `${timestamp}.${fileExtension}`;

            // Upload to S3
            const uploadedUrl = await S3Service.uploadToS3(selectedFile, fileName, 'avatar');

            // Complete progress
            clearInterval(progressInterval);
            setUploadProgress(100);

            // Set upload result
            setUploadResult({
                success: true,
                fileName: selectedFile.name,
                uploadedFileName: fileName,
                url: uploadedUrl,
                size: selectedFile.size,
                type: selectedFile.type
            });

            showSnackbar('File uploaded successfully!', 'success');

        } catch (error) {
            Logger.error('Upload error:', error);
            setUploadResult({
                success: false,
                fileName: selectedFile.name,
                error: error.message
            });
            showSnackbar(error.message || 'Upload failed', 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleClear = () => {
        // Clean up preview URL to prevent memory leaks
        if (filePreview?.url) {
            URL.revokeObjectURL(filePreview.url);
        }
        
        setSelectedFile(null);
        setFilePreview(null);
        setUploadResult(null);
        setUploadProgress(0);
    };

    const handleDelete = async () => {
        if (!uploadResult?.success || !uploadResult?.uploadedFileName) {
            showSnackbar('No file to delete', 'error');
            return;
        }

        try {
            setDeleting(true);

            // Delete from S3
            await S3Service.deleteFromS3(uploadResult.uploadedFileName, 'uploads');

            // Clear the upload result
            setUploadResult(null);
            setSelectedFile(null);
            setUploadProgress(0);

            showSnackbar('File deleted successfully!', 'success');
        } catch (error) {
            Logger.error('Delete error:', error);
            showSnackbar(error.message || 'Failed to delete file', 'error');
        } finally {
            setDeleting(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileTypeIcon = (fileType) => {
        if (fileType.startsWith('image/')) return <ImageIcon />;
        if (fileType.startsWith('video/')) return <VideoIcon />;
        if (fileType.startsWith('audio/')) return <AudioIcon />;
        return <DocumentIcon />;
    };

    const renderFilePreview = () => {
        if (!filePreview) return null;

        const { url, type } = filePreview;

        if (type.startsWith('image/')) {
            return (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Image Preview:
                    </Typography>
                    <Box sx={{ 
                        border: '2px dashed #e0e0e0',
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center',
                        backgroundColor: '#fafafa'
                    }}>
                        <img 
                            src={url} 
                            alt="File preview" 
                            style={{ 
                                maxWidth: '100%', 
                                maxHeight: '300px',
                                objectFit: 'contain',
                                borderRadius: '8px'
                            }}
                        />
                    </Box>
                </Box>
            );
        }

        if (type.startsWith('video/')) {
            return (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Video Preview:
                    </Typography>
                    <Box sx={{ 
                        border: '2px dashed #e0e0e0',
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center',
                        backgroundColor: '#fafafa'
                    }}>
                        <video 
                            src={url} 
                            controls 
                            style={{ 
                                maxWidth: '100%', 
                                maxHeight: '300px',
                                borderRadius: '8px'
                            }}
                        >
                            Your browser does not support video preview.
                        </video>
                    </Box>
                </Box>
            );
        }

        if (type.startsWith('audio/')) {
            return (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Audio Preview:
                    </Typography>
                    <Box sx={{ 
                        border: '2px dashed #e0e0e0',
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center',
                        backgroundColor: '#fafafa'
                    }}>
                        <audio src={url} controls style={{ width: '100%' }}>
                            Your browser does not support audio preview.
                        </audio>
                    </Box>
                </Box>
            );
        }

        // For other file types, show file icon
        return (
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    File Preview:
                </Typography>
                <Box sx={{ 
                    border: '2px dashed #e0e0e0',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: '#fafafa'
                }}>
                    <Box sx={{ fontSize: '4rem', color: 'text.secondary', mb: 1 }}>
                        {getFileTypeIcon(type)}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Preview not available for this file type
                    </Typography>
                </Box>
            </Box>
        );
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                File Upload
            </Typography>

            <Grid container spacing={3}>
                {/* File Selection */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Select File
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <input
                                accept="*/*"
                                style={{ display: 'none' }}
                                id="file-upload"
                                type="file"
                                onChange={handleFileSelect}
                                disabled={uploading}
                            />
                            <label htmlFor="file-upload">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<CloudUploadIcon />}
                                    disabled={uploading}
                                    sx={{ mb: 2 }}
                                >
                                    Choose File
                                </Button>
                            </label>
                        </Box>

                        {selectedFile && (
                            <Card sx={{ mb: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <FilePresentIcon sx={{ mr: 1, color: 'primary.main' }} />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {selectedFile.name}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={formatFileSize(selectedFile.size)}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={selectedFile.type || 'Unknown'}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        )}

                        {/* File Preview */}
                        {renderFilePreview()}

                        {/* Upload Progress */}
                        {uploading && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Uploading... {uploadProgress}%
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={uploadProgress}
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                            </Box>
                        )}

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="contained"
                                startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                                onClick={handleUpload}
                                disabled={!selectedFile || uploading}
                                sx={{ minWidth: 120 }}
                            >
                                {uploading ? 'Uploading...' : 'Upload'}
                            </Button>

                            {selectedFile && !uploading && (
                                <Button
                                    variant="outlined"
                                    onClick={handleClear}
                                >
                                    Clear
                                </Button>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* Upload Result */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Upload Result
                        </Typography>

                        {uploadResult ? (
                            <Box>
                                {uploadResult.success ? (
                                    <Alert
                                        severity="success"
                                        icon={<CheckCircleIcon />}
                                        sx={{ mb: 2 }}
                                    >
                                        File uploaded successfully!
                                    </Alert>
                                ) : (
                                    <Alert
                                        severity="error"
                                        icon={<ErrorIcon />}
                                        sx={{ mb: 2 }}
                                    >
                                        Upload failed: {uploadResult.error}
                                    </Alert>
                                )}

                                {uploadResult.success && (
                                    <Card>
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                                Upload Details
                                            </Typography>

                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Original File:
                                                </Typography>
                                                <Typography variant="body1">
                                                    {uploadResult.fileName}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Uploaded As:
                                                </Typography>
                                                <Typography variant="body1">
                                                    {uploadResult.uploadedFileName}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    File Size:
                                                </Typography>
                                                <Typography variant="body1">
                                                    {formatFileSize(uploadResult.size)}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    File Type:
                                                </Typography>
                                                <Typography variant="body1">
                                                    {uploadResult.type}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Upload URL:
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        wordBreak: 'break-all',
                                                        backgroundColor: '#f5f5f5',
                                                        p: 1,
                                                        borderRadius: 1,
                                                        fontFamily: 'monospace'
                                                    }}
                                                >
                                                    {uploadResult.url}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => navigator.clipboard.writeText(uploadResult.url)}
                                                >
                                                    Copy URL
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                                                    onClick={handleDelete}
                                                    disabled={deleting}
                                                >
                                                    {deleting ? 'Deleting...' : 'Delete File'}
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                )}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No upload result yet. Select a file and click upload to see the result.
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FileUploadMain;
